import { useCallback, useMemo, useState } from 'react';

import type { Category } from '@givebacks/api/offers';
import { useListOfferCategories } from '@givebacks/api/offers';
import { useSearchParams } from 'react-router-dom';
import { Box, ButtonMantine, Card, Collapse, Group, ScrollArea, Stack, Text, Title } from 'ui';

import classes from './CategoryFilterCard.module.css';

const Category = ({
  name,
  uuid,
  categoryId,
}: {
  name: string;
  uuid: string;
  categoryId?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClick = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (uuid) {
      nextParams.set('category', uuid);
    } else {
      nextParams.delete('category');
    }
    setSearchParams(nextParams);
  }, [uuid, searchParams, setSearchParams]);

  return (
    <Box onClick={handleClick} data-active={uuid === categoryId} className={classes.option}>
      {name}
    </Box>
  );
};

const CategoryList = ({
  categoryId,
  categories,
}: {
  categoryId?: string;
  categories?: Category[];
}) => {
  if (!categories) return null;
  return categories.map((category) => (
    <Category
      key={category.uuid}
      name={category.name}
      uuid={category.uuid}
      categoryId={categoryId}
    />
  ));
};

const CategoryLists = ({ categoryId }: { categoryId?: string }) => {
  const [showAll, setShowAll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const { data } = useListOfferCategories({
    queryOptions: {
      searches: {
        dining: {
          value: false,
        },
      },
    },
  });

  const firstTwelveCategories = useMemo(() => data?.categories.slice(0, 12), [data?.categories]);
  const categoriesList = useMemo(() => data?.categories.slice(12), [data?.categories]);

  const handleAllCategoriesClick = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('category');
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  return (
    <>
      <Stack gap={0}>
        <ScrollArea.Autosize
          style={{
            maxHeight: 'calc(100vh - var(--app-shell-header-height) - 200px)',
          }}
        >
          <Box
            onClick={handleAllCategoriesClick}
            data-active={!categoryId}
            className={classes.option}
          >
            <Text fw={600} c={!categoryId ? 'gray.8' : 'gray'}>
              {`All Online Offers`}
            </Text>
          </Box>
          <CategoryList categoryId={categoryId} categories={firstTwelveCategories} />
          <Collapse in={showAll}>
            <CategoryList categoryId={categoryId} categories={categoriesList} />
          </Collapse>
        </ScrollArea.Autosize>
      </Stack>
      <Group justify="flex-end">
        <ButtonMantine size="sm" variant="subtle" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'See Less' : 'See More'}
        </ButtonMantine>
      </Group>
    </>
  );
};

const CategoryFilterCard = () => {
  const [searchParams] = useSearchParams();
  const categoryId = searchParams.get('category') ?? undefined;

  return (
    <Card className={classes.card} pos="sticky">
      <Stack>
        <Title order={2}>Categories</Title>
        <CategoryLists categoryId={categoryId} />
      </Stack>
    </Card>
  );
};

export { CategoryFilterCard };
